<template>
  <div class="rb-ob-form rb-match-page rb-match-results-page">
    <header>
      <div class="rb-ob-progress" :aria-label="$t('intake.progressLabel')">
        <div class="rb-ob-progress-meta">
          <span>{{ progressMeta(9) }}</span>
          <span>{{ $t('intake.stepLabels.match') }}</span>
        </div>
        <div class="rb-ob-progress-bar" aria-hidden="true">
          <i :style="{ '--p': progressPercent(9) }"></i>
        </div>
      </div>
      <h1 class="rb-ob-title">{{ $t('intake.matchesTitle') }}</h1>
      <p class="rb-ob-q-hint">{{ $t('intake.matchesIntro') }}</p>
    </header>

    <div class="rb-understood-note">
      <span aria-hidden="true">✓</span>
      <p>{{ reflection || $t('intake.matchesUnderstood') }}</p>
    </div>
    <ul v-if="topicChips.length" class="rb-match-need-chips" :aria-label="$t('intake.stepLabels.topics')">
      <li v-for="item in topicChips" :key="item.need_ref" class="rb-match-need-chip">{{ item.approved_display_label }}</li>
    </ul>
    <p v-if="validating" class="rb-ob-status" role="status">{{ $t('intake.portal.loading') }}</p>
    <div v-else-if="validationError" role="alert">
      <p>{{ validationError }}</p>
      <button class="rb-ob-submit rb-ob-submit-secondary" type="button" @click="verifyRecommendation">{{ $route.params.lang === 'en' ? 'Check again' : 'بررسی دوباره' }}</button>
      <router-link class="btn btn-secondary" :to="{name:'portalSupport',params:{lang:$route.params.lang || 'fa'}}">{{ $route.params.lang === 'en' ? 'Contact support' : 'درخواست پشتیبانی' }}</router-link>
    </div>
    <p v-else class="rb-match-result-count" role="status">{{ $t('intake.matchUi.count', {count: recommendationCount}) }}</p>
    <p v-if="!validating && !validationError && !people.length" class="rb-ob-q-hint">{{ $t('intake.matchUi.empty') }}</p>

    <p v-if="selectedId" class="rb-match-selected-note" role="status">
      {{ $t('intake.therapistSelected') }}
    </p>

    <div class="rb-match-therapist-cards">
      <article
          v-for="person in people"
          :key="person.id"
          class="rb-match-therapist-card"
          :class="{
            'rb-recommended': person.recommended,
            'is-selected': selectedId === person.id,
          }"
      >
        <span v-if="person.recommended" class="rb-recommend-badge">
          {{ $t('intake.recommendBadge') }}
        </span>
        <div class="rb-match-therapist-card-head">
          <span class="rb-avatar rb-match-therapist-avatar" aria-hidden="true">
            <img
                v-if="person.photoUrl && !photoFailed[person.id]"
                :src="person.photoUrl"
                alt=""
                @error="onPhotoError(person.id)"
            >
            <template v-else>{{ person.initial }}</template>
          </span>
          <div class="rb-match-therapist-meta">
            <h2 class="rb-match-therapist-name">{{ personName(person) }}</h2>
            <p class="rb-match-therapist-role">
              {{ personTitle(person) }}
              <template v-if="personTitle(person) && personTier(person)"> · </template>
              {{ personTier(person) }}
            </p>
          </div>
        </div>
        <div class="rb-match-therapist-reason">
          <span class="rb-match-therapist-reason-label">{{ $t('intake.whyMatch') }}</span>
          <p>{{ personReason(person) }}</p>
        </div>
        <button class="rb-why-button" type="button" @click="openWhy(person)">
          {{ $t('intake.whyMatch') }}
        </button>
        <ul class="rb-match-therapist-facts">
          <li>
            <span class="rb-match-fact-label">{{ $t('intake.nearestSlot') }}</span>
            <b class="rb-match-fact-value">{{ nearestSlotLabel(person) }}</b>
          </li>
          <li>
            <span class="rb-match-fact-label">{{ $t('intake.sessionPrice') }}</span>
            <b class="rb-match-fact-value">{{ priceLabel(person) }}</b>
          </li>
        </ul>
        <div class="rb-match-therapist-actions">
          <button class="rb-ob-submit" type="button" @click="selectTherapist(person.id)">
            {{ $t('intake.selectTherapist') }}
          </button>
          <button
              class="rb-ob-submit rb-ob-submit-secondary"
              type="button"
              @click="viewProfile(person.id)"
          >
            {{ $t('intake.viewProfile') }}
          </button>
        </div>
      </article>
    </div>

    <div class="rb-ob-actions">
      <button class="rb-ob-submit rb-ob-submit-secondary" type="button" @click="declineAll">{{ $t('intake.matchUi.declineAll') }}</button>
      <p class="rb-ob-q-hint">{{ $t('intake.matchUi.declineHint') }}</p>
    </div>
    <aside class="rb-pricing-explainer">
      <div>
        <b>{{ $t('intake.pricingTitle') }}</b>
        <p>{{ $t('intake.pricingBody') }}</p>
      </div>
    </aside>
    <p class="rb-match-footnote">{{ $t('intake.matchesFootnote') }}</p>

    <div class="rb-ob-trust">
      <p>{{ $t('intake.trustPath') }}</p>
      <p>{{ $t('authUi.familyNote') }}</p>
    </div>

    <div
        v-if="whyPerson"
        class="rb-modal-layer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="why-title"
    >
      <div class="rb-modal-backdrop" @click="closeWhy"></div>
      <div class="rb-modal">
        <h2 id="why-title">
          {{ $t('intake.whyTitle', {name: personName(whyPerson)}) }}
        </h2>
        <p>{{ personReason(whyPerson) }}</p>
        <div v-if="personEvidence(whyPerson)" class="rb-evidence-box">
          <b>{{ $t('intake.evidenceTitle') }}</b>
          <p>{{ personEvidence(whyPerson) }}</p>
        </div>
        <button class="rb-ob-submit rb-ob-submit-secondary" type="button" @click="closeWhy">
          {{ $t('intake.close') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  loadIntake,
  saveIntake,
  hasSafetyRisk,
  isIntakeReadyToMatch,
  toFaDigits,
} from '@/intake/intakeState'
import {formatMoneyForDisplay, priceTierLabel} from '@/intake/formatMoney'
import {MOCK_MATCHES} from '@/intake/mockMatches'
import {isSandboxMatchEnabled, isMatchmakingV2ApiEnabled, getRecommendation, membersToPeople} from '@/intake/sandboxMatchApi'
import {hasSandboxMatchSession, loadMatchSession, saveMatchSession, invalidateMatchSession} from '@/intake/sandboxMatchSession'
import {loadScreeningStore} from '@/intake/screeningSession'
import {clientError} from '@/intake/clientMessages'

export default {
  name: 'MatchResultsStep',
  data() {
    const draft = loadIntake()
    let people = MOCK_MATCHES
    if (isSandboxMatchEnabled() && hasSandboxMatchSession()) {
      people = loadMatchSession().people
    }
    return {
      people,
      reflection: typeof (loadMatchSession() || {}).explanation === 'string' ? loadMatchSession().explanation : '',
      topicChips: (loadMatchSession() || {}).topicChips || [],
      whyPerson: null,
      selectedId: (draft && draft.therapistId) || '',
      photoFailed: {},
      validating: false, validationError: '', validationGeneration: 0,
    }
  },
  computed: {
    recommendationCount() {
      return this.$route.params.lang === 'en' ? String(this.people.length) : toFaDigits(this.people.length)
    },
  },
  created() {
    const draft = loadIntake()
    // V2 screening match: session people are authoritative — do not bounce to legacy draft gates.
    if (isSandboxMatchEnabled() && hasSandboxMatchSession()) {
      if (typeof isMatchmakingV2ApiEnabled === 'function' && isMatchmakingV2ApiEnabled()) {
        this.people = []
        this.verifyRecommendation()
        window.addEventListener('keydown', this.onKeydown)
        window.addEventListener('focus', this.verifyRecommendation)
        return
      }
      this.people = this.resolvePeople()
      window.addEventListener('keydown', this.onKeydown)
      return
    }
    if (!isIntakeReadyToMatch(draft)) {
      this.$router.replace({name: 'intakeScreening'})
      return
    }
    if (hasSafetyRisk(draft.safety)) {
      this.$router.replace({name: 'intakeCrisis'})
      return
    }
    if (isSandboxMatchEnabled() && !hasSandboxMatchSession()) {
      this.$router.replace({name: 'intakeMatching'})
      return
    }
    this.people = this.resolvePeople()
    window.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy() {
    this.validationGeneration += 1
    window.removeEventListener('keydown', this.onKeydown)
    window.removeEventListener('focus', this.verifyRecommendation)
  },
  methods: {
    async verifyRecommendation() {
      const generation = ++this.validationGeneration
      const cached = loadMatchSession() || {}
      const screening = loadScreeningStore()
      this.validating = true; this.validationError = ''; this.people = []; this.reflection = ''; this.topicChips = []; this.selectedId = ''
      try {
        if (!cached.recommendationId) {
          this.$router.replace({name:'intakeScreening',params:{lang:this.$route.params.lang || 'fa'}})
          return
        }
        if (screening.intakeId && cached.screeningIntakeId && String(screening.intakeId) !== String(cached.screeningIntakeId)) {
          this.$router.replace({name:screening.snapshotId?'intakeMatching':'intakeScreening',params:{lang:this.$route.params.lang || 'fa'}})
          return
        }
        const rec = await getRecommendation(cached.recommendationId)
        if (generation !== this.validationGeneration) return
        const current = loadMatchSession()
        if (!current || current.recommendationId !== cached.recommendationId) return
        const people = membersToPeople(rec.members,this.$route.params.lang || 'fa',cached.displayTimezone)
        this.people = people
        this.reflection = rec.explanation && rec.explanation.client_reflection_rendered_fa || ''
        this.topicChips = rec.explanation && rec.explanation.selected_need_display_items || []
        saveMatchSession({people,expiresAt:rec.expires_at || cached.expiresAt})
      } catch (error) {
        if (generation !== this.validationGeneration) return
        if (error && error.code === 'SAFETY_CLEARANCE_REQUIRED') invalidateMatchSession({recommendationId:cached.recommendationId})
        this.validationError = clientError(error,this.$route.params.lang || 'fa')
      } finally { if (generation === this.validationGeneration) this.validating = false }
    },
    progressPercent() { return '67%' },
    declineAll() {
      const session = loadMatchSession() || {}
      this.$router.push({name: 'portalRequestNew', params: {lang: this.$route.params.lang || 'fa'}, query: {topic: 'BOOKING', from: 'match-declined', recommendation: session.recommendationId || ''}})
    },
    toFaDigits,
    resolvePeople() {
      if (isSandboxMatchEnabled() && hasSandboxMatchSession()) {
        return loadMatchSession().people.filter((person) => typeof person.explanation_rendered_fa === 'string' && person.explanation_rendered_fa.trim())
      }
      return MOCK_MATCHES
    },
    personName(person) {
      if (person.name) {
        return person.name
      }
      return this.$t('intake.therapist.' + person.id + '.name')
    },
    personTitle(person) {
      if (person.title && !/^tier_\d+$/i.test(person.title)) {
        return person.title
      }
      // Mock therapists only (t1/t2/t3); never invent live therapist id keys.
      if (person.sandbox && /^t\d+$/i.test(String(person.id))) {
        return this.$t('intake.therapist.' + person.id + '.title')
      }
      return ''
    },
    personTier(person) {
      if (person.tier) {
        return priceTierLabel(person.tier, this.$route.params.lang || 'fa')
      }
      if (person.sandbox && /^t\d+$/i.test(String(person.id))) {
        return this.$t('intake.therapist.' + person.id + '.tier')
      }
      return ''
    },
    personReason(person) {
      if (person.reason) {
        return person.reason
      }
      if (person.explanation_rendered_fa) {
        return person.explanation_rendered_fa
      }
      return person.sandbox && /^t\d+$/.test(person.id) ? this.$t('intake.therapist.' + person.id + '.reason') : ''
    },
    personEvidence(person) {
      if (person.evidence) {
        return person.evidence
      }
      return person.sandbox && /^t\d+$/.test(person.id) ? this.$t('intake.therapist.' + person.id + '.evidence') : ''
    },
    priceLabel(person) {
      const money = formatMoneyForDisplay(person.price, person.currency)
      if (money.isToman || money.unit === 'raw') {
        return money.text + ' ' + this.$t('intake.toman')
      }
      if (money.currency) {
        return money.text + ' ' + money.currency
      }
      return money.text
    },
    onPhotoError(id) {
      this.$set(this.photoFailed, id, true)
    },
    nearestSlotLabel(person) {
      if (person.nearestLabel) {
        return person.nearestLabel
      }
      if (person.nearestKey) {
        return this.$t('intake.matchSlot.' + person.nearestKey)
      }
      return '—'
    },
    progressMeta() {
      return this.$t('intake.progressMeta', {
        step: this.toFaDigits(2),
        total: this.toFaDigits(3),
      })
    },
    onKeydown(event) {
      if (event.key === 'Escape') {
        this.closeWhy()
      }
    },
    openWhy(person) {
      this.whyPerson = person
    },
    closeWhy() {
      this.whyPerson = null
    },
    selectTherapist(id) {
      if (this.validating || this.validationError) return
      this.selectedId = id
      saveIntake({therapistId: id})
      this.$router.push({
        name: 'intakeSlots',
        params: {
          lang: this.$route.params.lang || 'fa',
          therapistId: id,
        },
      })
    },
    viewProfile(id) {
      if (this.validating || this.validationError) return
      this.selectedId = id
      saveIntake({therapistId: id})
      this.$router.push({
        name: 'intakeTherapist',
        params: {
          lang: this.$route.params.lang || 'fa',
          therapistId: id,
        },
      })
    },
  },
}
</script>
